import { useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Spinner, StatusPill } from '../../../ui'
import { LETTER_LINKING_EXPLAINER } from '../commsShared/linkStates'
import { DOCUMENT_LABEL, LetterWithReplies, ReplyDetail, SEND_KIND_LABEL, formatDateTime, humanise, openSendDocument, sendKindLabel, sendStatusTone } from './shared'
import styles from './styles.module.scss'

// Letters on a claim change rarely; cache per claim like the case bundle.
const lettersCache = new Map<string, LetterWithReplies[]>()
export const invalidateLetters = (claimRef?: string | null) => {
    if (claimRef) lettersCache.delete(claimRef)
    else lettersCache.clear()
}

const KIND_ORDER = Object.keys(SEND_KIND_LABEL)

/**
 * Letter linking: which of our letters, by type, this email answers. Kept
 * apart from "who is this about" (CaseSummaryCard) because a claim will carry
 * a letter of complaint, chasers, a settlement response and more over time,
 * and reporting needs replies grouped by the type of letter they answer.
 */
export const LetterLinkCard = ({
    detail,
    busy,
    onChoose,
    onOpenSend,
}: {
    detail: ReplyDetail
    busy: boolean
    /** sendId null = "not about a specific letter". */
    onChoose: (sendId: string | null) => void
    onOpenSend: (sendId: string) => void
}) => {
    const claimRef = detail.case?.claimRef || detail.claimRef || null
    const [letters, setLetters] = useState<LetterWithReplies[] | null>(claimRef ? lettersCache.get(claimRef) ?? null : null)
    const [loading, setLoading] = useState(Boolean(claimRef) && !lettersCache.has(claimRef || ''))
    const [error, setError] = useState<string | null>(null)
    const [showAll, setShowAll] = useState(false)

    useEffect(() => {
        if (!claimRef) return
        const cached = lettersCache.get(claimRef)
        if (cached) {
            setLetters(cached)
            setLoading(false)
            return
        }
        let cancelled = false
        setLoading(true)
        setError(null)
        api({ url: 'comms/inbox/letters', data: { claimRef } })
            .then((resp) => {
                if (cancelled) return
                if (resp.data?.success) {
                    const rows: LetterWithReplies[] = resp.data.letters || []
                    lettersCache.set(claimRef, rows)
                    setLetters(rows)
                } else setError(resp.data?.message || 'Could not load the letters on this claim.')
            })
            .catch((err: any) => !cancelled && setError(err?.response?.data?.message || 'Could not load the letters on this claim.'))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [claimRef, detail.matchedSendId])

    if (!claimRef) return null

    const chosenId = detail.matchedSendId
    const chosen = letters?.find((l) => l.id === chosenId) ?? null
    const how = detail.letter?.how ?? null
    const sorted = [...(letters ?? [])].sort((a, b) => KIND_ORDER.indexOf(a.sendKind) - KIND_ORDER.indexOf(b.sendKind) || String(b.sentAt || b.queuedAt || '').localeCompare(String(a.sentAt || a.queuedAt || '')))
    const grouped = sorted.reduce<Map<string, LetterWithReplies[]>>((acc, l) => {
        const k = l.sendKind || 'initial_loc'
        if (!acc.has(k)) acc.set(k, [])
        acc.get(k)!.push(l)
        return acc
    }, new Map())
    const manyLetters = sorted.length > 3
    const visibleGroups = manyLetters && !showAll && chosen ? new Map([[chosen.sendKind || 'initial_loc', [chosen]]]) : grouped

    const subtitle = chosenId
        ? how === 'exact'
            ? `Answers our ${sendKindLabel(chosen?.sendKind ?? detail.letter?.sendKind).toLowerCase()} directly (reply header, reply address or footer reference).`
            : how === 'inferred'
              ? `Assumed to be about our ${sendKindLabel(chosen?.sendKind ?? detail.letter?.sendKind).toLowerCase()}, the only obvious letter on this claim. Change it if the email is about another letter.`
              : `Chosen by ${detail.letter?.by?.replace(/^human:/, '') || 'a team member'}.`
        : sorted.length
          ? 'Not tied to a specific letter. Pick the letter this email answers, if any.'
          : 'No letters have been sent on this claim yet.'

    return (
        <Card
            padding="sm"
            title="Which of our letters is this about?"
            subtitle={subtitle}
            actions={
                <div className={styles.actionRow}>
                    {chosenId && (
                        <Button size="sm" onClick={() => onOpenSend(chosenId)} title="The email and letter we sent that this reply answers">
                            Original email
                        </Button>
                    )}
                    {chosenId && (
                        <Button size="sm" variant="link" disabled={busy} onClick={() => onChoose(null)} title="Keep the client and claim; just drop the letter">
                            Not about a specific letter
                        </Button>
                    )}
                    {manyLetters && chosen && (
                        <Button size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                            {showAll ? 'Show chosen only' : `Show all ${sorted.length} letters`}
                        </Button>
                    )}
                </div>
            }
        >
            {loading ? (
                <Spinner block text="Loading letters…" />
            ) : error ? (
                <Banner tone="bad">{error}</Banner>
            ) : !sorted.length ? (
                <p className={styles.mutedInline}>{LETTER_LINKING_EXPLAINER}</p>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Letter type</th>
                            <th>Sent</th>
                            <th>To</th>
                            <th>Delivery</th>
                            <th>Replies</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from(visibleGroups.entries()).map(([kind, rows]) =>
                            rows.map((l, i) => {
                                const isChosen = l.id === chosenId
                                return (
                                    <tr key={l.id} className={isChosen ? styles.rowSelected : undefined}>
                                        <td>
                                            {i === 0 ? <strong>{sendKindLabel(kind)}</strong> : <span className={styles.mutedInline}>{sendKindLabel(kind)}</span>}
                                            {isChosen && (
                                                <>
                                                    {' '}
                                                    <StatusPill size="sm" tone="good" dot>
                                                        this one
                                                    </StatusPill>
                                                </>
                                            )}
                                        </td>
                                        <td>{formatDateTime(l.sentAt || l.queuedAt)}</td>
                                        <td className={styles.mono}>{l.toEmail}</td>
                                        <td>
                                            <span className={styles.chips}>
                                                <StatusPill size="sm" tone={sendStatusTone(l.status)} dot>
                                                    {humanise(l.status)}
                                                </StatusPill>
                                                {l.openCount > 0 && <StatusPill size="sm">opened {l.openCount}×</StatusPill>}
                                            </span>
                                        </td>
                                        <td>{l.replies.length ? `${l.replies.length}` : <span className={styles.mutedInline}>none</span>}</td>
                                        <td className={styles.tdActions}>
                                            {!isChosen && (
                                                <Button size="sm" variant="primary" disabled={busy} onClick={() => onChoose(l.id)} title="This email answers this letter">
                                                    This one
                                                </Button>
                                            )}
                                            {l.documents.map((d) => (
                                                <Button key={d} size="sm" variant="ghost" onClick={() => openSendDocument(l.id, d)} title={`Open the ${DOCUMENT_LABEL[d]} PDF`}>
                                                    {DOCUMENT_LABEL[d]} PDF
                                                </Button>
                                            ))}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            )}
        </Card>
    )
}
